import React, { useEffect, useState } from "react";
import {
  Bell, MessageCircle, Send, PlayCircle, PackageX, ReceiptText,
  CheckCircle2, Clock, AlertTriangle, PauseCircle, RefreshCw, Eraser, CalendarDays, Cake, Landmark,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const statusMap = {
  enviado: { label: "Enviado", cls: "text-emerald-700 bg-emerald-50", Icon: CheckCircle2 },
  pendente: { label: "Pendente", cls: "text-amber-700 bg-amber-50", Icon: Clock },
  pausado: { label: "Pausado", cls: "text-slate-600 bg-slate-100", Icon: PauseCircle },
  erro: { label: "Erro", cls: "text-red-700 bg-red-50", Icon: AlertTriangle },
};

const Notificacoes = () => {
  const { getNotificacoes, limparNotificacoes, enviarNotificacao, runFechamento, getWhatsappStatus } = useApp();
  const { toast } = useToast();
  const [lista, setLista] = useState([]);
  const [config, setConfig] = useState(true);
  const [loading, setLoading] = useState(true);
  const hoje = new Date().toISOString().slice(0, 10);
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [filtroAtivo, setFiltroAtivo] = useState(false);

  const carregar = async (params = {}) => {
    setLoading(true);
    const [n, s] = await Promise.all([getNotificacoes(params), getWhatsappStatus()]);
    setLista(n);
    setConfig(s.configurado);
    setLoading(false);
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  const aplicarFiltro = () => {
    if (!de && !ate) return toast({ title: "Informe a data inicial e/ou final." });
    setFiltroAtivo(true);
    carregar({ de: de || ate, ate: ate || de });
  };
  const verHoje = () => { setDe(""); setAte(""); setFiltroAtivo(false); carregar(); };
  const handleLimpar = async () => {
    if (!window.confirm("Limpar a lista de hoje? As notificações continuam gravadas e podem ser consultadas pelo filtro de período.")) return;
    await limparNotificacoes();
    toast({ title: "Lista limpa", description: "As notificações já vistas foram ocultadas da lista do dia." });
    verHoje();
  };

  const handleEnviar = async (id) => {
    const r = await enviarNotificacao(id);
    toast({
      title: r.status === "enviado" ? "Mensagem enviada" : "Envio não concluído",
      description: r.status === "enviado" ? "WhatsApp enviado com sucesso."
        : (r.dica ? `${r.dica}` : (r.erro || "Envio pausado — configure o WhatsApp.")),
    });
    carregar();
  };

  const handleFechamento = async () => {
    const r = await runFechamento();
    toast({ title: "Rotina executada", description: `${r.criadas} fatura(s) fechada(s) e enfileirada(s).` });
    carregar();
  };

  const tipoInfo = (t) => ({
    fatura: { label: "Fatura", Icon: ReceiptText, tint: "bg-violet-50 text-violet-600" },
    boleto: { label: "Boleto", Icon: Landmark, tint: "bg-blue-50 text-blue-600" },
    lembrete: { label: "Lembrete", Icon: Clock, tint: "bg-sky-50 text-sky-600" },
    aniversario: { label: "Aniversário", Icon: Cake, tint: "bg-pink-50 text-pink-600" },
  }[t] || { label: "Estoque mínimo", Icon: PackageX, tint: "bg-amber-50 text-amber-600" });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Notificações (WhatsApp)</h1>
          <p className="text-slate-400 text-sm mt-0.5">Alertas ao familiar (estoque com 1 unidade ou menos), faturas, boletos, lembretes e aniversários</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={carregar}><RefreshCw className="w-4 h-4" /> Atualizar</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={handleFechamento}>
            <PlayCircle className="w-4 h-4" /> Executar fechamento agora
          </Button>
        </div>
      </div>

      {!config && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3.5">
          <MessageCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <b>Envio pausado.</b> O WhatsApp (Meta Cloud API) ainda não está configurado. As mensagens ficam na fila com status "Pausado".
            Configure <code>WHATSAPP_ACCESS_TOKEN</code> e <code>WHATSAPP_PHONE_NUMBER_ID</code> no backend e aprove os templates na Meta para ativar o envio automático.
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm px-5 py-3 flex items-center gap-3 flex-wrap" data-testid="notif-filtro">
        <CalendarDays className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-600">Consultar período:</span>
        <input type="date" value={de} max={hoje} onChange={(e) => setDe(e.target.value)} className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm" data-testid="notif-de" />
        <span className="text-slate-400 text-sm">até</span>
        <input type="date" value={ate} max={hoje} onChange={(e) => setAte(e.target.value)} className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm" data-testid="notif-ate" />
        <Button variant="outline" size="sm" onClick={aplicarFiltro} data-testid="notif-filtrar">Consultar</Button>
        {filtroAtivo && <Button variant="ghost" size="sm" onClick={verHoje} data-testid="notif-hoje">Voltar para hoje</Button>}
        <Button variant="outline" size="sm" className="ml-auto gap-1.5 text-slate-600" onClick={handleLimpar} disabled={filtroAtivo || lista.length === 0} data-testid="notif-limpar">
          <Eraser className="w-4 h-4" /> Limpar lista
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Bell className="w-4 h-4 text-blue-600" />
          <h2 className="font-semibold text-slate-800">{filtroAtivo ? `Notificações de ${(de || ate).split("-").reverse().join("/")} a ${(ate || de).split("-").reverse().join("/")}` : "Notificações de hoje"}</h2>
          <span className="ml-auto text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md" data-testid="notif-count">{lista.length}</span>
        </div>
        <div className="divide-y divide-slate-50">
          {loading && <p className="text-sm text-slate-400 text-center py-8">Carregando...</p>}
          {!loading && lista.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-10">{filtroAtivo ? "Nenhuma notificação no período." : "Nenhuma notificação hoje. Use o filtro acima para consultar dias anteriores."}</p>
          )}
          {lista.map((n) => {
            const ti = tipoInfo(n.tipo);
            const st = statusMap[n.status] || statusMap.pendente;
            return (
              <div key={n.id} className="flex items-start gap-3 px-5 py-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${ti.tint}`}>
                  <ti.Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">{n.familiarNome || "—"}</span>
                    <span className="text-xs text-slate-400">{n.familiarTelefone}</span>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded ${st.cls}`}>
                      <st.Icon className="w-3 h-3" /> {st.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{n.mensagem}</p>
                  {n.erro && <p className="text-xs text-red-500 mt-1">{n.erro}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">Criada em {n.dataCriacao?.replace("T", " ")}{n.dataEnvio ? ` · Enviada ${n.dataEnvio.replace("T", " ")}` : ""}</p>
                </div>
                {n.status !== "enviado" && (
                  <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={() => handleEnviar(n.id)}>
                    <Send className="w-3.5 h-3.5" /> Enviar
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Notificacoes;
